<template>
    <div class="rounded-3xl p-8" :class="[
        isHighlighted
            ? 'ring-2 ring-blue-500'
            : 'ring-1 ring-gray-700'
    ]">
        <!-- Column Name & Description -->
        <h3 :id="'column-' + column.name.toLowerCase()" class="text-lg/8 font-bold" :class="[
            isHighlighted ? 'text-blue-400' : 'text-white'
        ]">
            {{ column.name }}
        </h3>
        <p v-if="showDescription && column.description" class="mt-4 text-sm/6 text-gray-400 lg:min-h-20 xl:min-h-0">
            {{ column.description }}
        </p>

        <!-- Price Section - Only if isPricing is true -->
        <template v-if="isPricing && !hidePrice && column.price !== undefined">
            <p class="mt-6 flex items-baseline gap-x-1">
                <span class="text-4xl font-semibold tracking-tight text-white">
                    {{ formatPrice(column.price) }}
                </span>
                <span v-if="column.priceUnit" class="text-sm/6 font-semibold text-gray-400">{{ column.priceUnit
                    }}</span>
            </p>
        </template>

        <!-- CTA Button -->
        <template v-if="!hideCta && column.cta">
            <a :data-action="'click-' + column.name.toLowerCase()" @click.prevent="$emit('columnClick', column)"
                :aria-describedby="column.name.toLowerCase() + '-column'"
                class="mt-6 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold cursor-pointer" :class="[
                    isHighlighted
                        ? 'bg-blue-500 text-white hover:bg-blue-400'
                        : 'text-blue-400 ring-1 ring-inset ring-blue-500/20 hover:ring-blue-500'
                ]">
                {{ column.cta }}
            </a>
        </template>

        <!-- Ratings -->
        <Ratings v-if="column.rating" :rating="column.ratings" />

        <!-- Features/Content List -->
        <div class="mt-8">
            <slot name="features"></slot>
        </div>
    </div>
</template>

<script lang="ts">
export type IColumn<T extends string | number | symbol = string> = Record<T, any> & {
    name: string;
    description?: string;
    price?: number | string;
    priceUnit?: string;
    cta?: string;
    isHighlighted?: boolean;
    rating?: number;
}

export default {
    name: 'Column',
    props: {
        column: {
            type: Object as PropType<IColumn>,
            required: true
        },
        showDescription: {
            type: Boolean,
            default: true
        },
        hidePrice: {
            type: Boolean,
            default: false
        },
        hideCta: {
            type: Boolean,
            default: false
        },
        isPricing: {
            type: Boolean,
            default: false
        }
    },
    emits: ['columnClick'],
    setup(props) {
        const isHighlighted = computed(() => props.column.isHighlighted || false);

        const formatPrice = (price: number | string): string => {
            if (typeof price === 'number') {
                const formattedNumber = price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                return '$' + formattedNumber + (price > 0 ? '+' : '');
            }
            return price.toString();
        };

        return {
            isHighlighted,
            formatPrice
        };
    }
};
</script>